import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAudio } from '../../../hooks/useAudio';
import { useSubAlarms } from '../../../hooks/useSubAlarms';
import { useTheme } from '../../../hooks/useTheme';
import { useTimerBase } from '../../../hooks/useTimer';
import { useProfileStore } from '../../../store/profileStore';
import { useSelectedTimerStore } from '../../../store/selectedTimerStore';
import { useSettingsStore } from '../../../store/settingsStore';
import { BaseTimerData } from '../../../store/types/timer';
import { FinishOverride, handleDragEvent, handleFinish } from '../../../utils/timerHandler';
import { getProgressColor } from '../../../utils/progressColor';
import { getBonusVideoSrc } from '../../../config/theme/themeExtras';
import BonusVideoOverlay from '../../common/BonusVideoOverlay';
import { PhaseSegment } from '../../common/TimerFace';
import ControlButtons from '../shared/controls/ControlButtons';
import HomeButton from '../shared/controls/HomeButton';
import MuteButton from '../shared/controls/MuteButton';
import ProfileQuickSwitch from '../shared/controls/ProfileQuickSwitch';
import UnitSwitch from '../shared/controls/UnitSwitch';
import TimeDisplay from '../shared/displays/TimeDisplay';
import TimerDisplay from '../shared/displays/TimerDisplay';
import TimerContent, { TimerContentProps } from '../shared/TimerContent';

const PHASE_PALETTE = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFE66D', '#96CEB4', '#DDA0DD', '#F08A5D', '#6C5B7B', '#F7DC6F', '#82CCDD'];

const BaseTimer: React.FC<{ timer: BaseTimerData }> = ({ timer }) => {
    const { defaultTimer, selectDefaultTimer, updateDefaultTimer } = useSelectedTimerStore();
    const audio = useAudio();

    const activeProfile = useProfileStore((state) =>
        state.profiles.find((p) => p.id === state.activeProfileId)
    );

    // Sub-alarms only apply to the default (base) timer when a profile is active
    // AND the timer's set duration matches the profile's total. This prevents
    // sub-alarm misfiring if the user drags the timer to a different duration.
    const isDefaultTimer = timer.id === defaultTimer.id;
    const profileForSubAlarms =
        isDefaultTimer && activeProfile && timer.time === activeProfile.totalMinutes ? activeProfile : null;

    const { time: storedTime, isMinutes: storedIsMinutes, pointColorIndex, title } = timer;

    const { selectedThemeCopy } = useTheme(pointColorIndex, title);

    const onFinish = useCallback(
        (reset: () => void) => {
            // If the active profile has a final sub-alarm (atElapsedMin === totalMinutes),
            // use its custom sound + label for the finish popup.
            let override: FinishOverride | undefined;
            if (profileForSubAlarms) {
                const finalSub = profileForSubAlarms.subAlarms.find(
                    (s) => s.atElapsedMin >= profileForSubAlarms.totalMinutes
                );
                if (finalSub) {
                    override = {
                        soundSrc: finalSub.soundSrc || undefined,
                        title: finalSub.label,
                        text: `${profileForSubAlarms.totalMinutes} min routine complete! 🎉`,
                        emoji: finalSub.emoji,
                    };
                }
            }
            handleFinish(timer, audio, selectedThemeCopy.color.point, reset, override);
        },
        [timer, audio, selectedThemeCopy, profileForSubAlarms]
    );

    const {
        totalTime,
        count,
        progress,
        currentTime,
        isRunning,
        isMinutes,
        isInitialized,
        start,
        stop,
        reset,
        toggleUnit,
        setTime,
        add,
    } = useTimerBase({
        timer,
        initialTime: storedTime,
        isMinutes: storedIsMinutes,
        onFinish,
    });

    // Fire sub-alarm sounds + toasts at elapsed thresholds.
    const activePhase = useSubAlarms({
        profile: profileForSubAlarms,
        totalMinutes: profileForSubAlarms?.totalMinutes ?? storedTime,
        count,
        isRunning,
        audio,
    });

    // Override the theme point color when a phase is active (if that phase
    // specifies a custom pointColor). This drives the timer ring color.
    // Also apply color-by-progress (green→yellow→red) if enabled and no
    // phase color override is set.
    const { progressColorEnabled } = useSettingsStore();
    const themedForDisplay = useMemo(() => {
        if (activePhase?.pointColor) {
            return {
                ...selectedThemeCopy,
                color: { ...selectedThemeCopy.color, point: activePhase.pointColor },
            };
        }
        if (progressColorEnabled && isRunning) {
            return {
                ...selectedThemeCopy,
                color: { ...selectedThemeCopy.color, point: getProgressColor(progress) },
            };
        }
        return selectedThemeCopy;
    }, [selectedThemeCopy, activePhase, progressColorEnabled, isRunning, progress]);

    const phases = useMemo<PhaseSegment[] | undefined>(() => {
        const p = activeProfile || profileForSubAlarms;
        if (!p || p.subAlarms.length === 0) return undefined;
        const subs = [...p.subAlarms]
            .filter((s) => s.atElapsedMin > 0)
            .sort((a, b) => a.atElapsedMin - b.atElapsedMin);
        if (subs.length === 0) return undefined;
        const total = p.totalMinutes;
        const segs: PhaseSegment[] = [];
        let prevMin = 0;
        subs.forEach((sub, i) => {
            const endFrac = Math.min(sub.atElapsedMin / total, 1);
            if (endFrac > prevMin / total) {
                segs.push({
                    startFraction: prevMin / total,
                    endFraction: endFrac,
                    color: sub.pointColor || PHASE_PALETTE[i % PHASE_PALETTE.length],
                });
            }
            prevMin = sub.atElapsedMin;
        });
        if (prevMin < total) {
            segs.push({
                startFraction: prevMin / total,
                endFraction: 1,
                color: themedForDisplay.color.point,
            });
        }
        return segs.length > 1 ? segs : undefined;
    }, [activeProfile, profileForSubAlarms, themedForDisplay]);

    useEffect(() => {
        if (isDefaultTimer) {
            updateDefaultTimer({ time: totalTime });
        }
    }, [totalTime, updateDefaultTimer, isDefaultTimer]);

    useEffect(() => {
        if (isDefaultTimer) {
            updateDefaultTimer({ isMinutes });
        }
    }, [isMinutes, updateDefaultTimer, isDefaultTimer]);

    const handleDragOrClick = (e: React.MouseEvent | React.TouchEvent) => {
        if (isDefaultTimer) {
            handleDragEvent(e, setTime);
        }
    };

    // Celebration overlay state (shown when user finishes early via "Done!" button)
    const [celebrationVideoSrc, setCelebrationVideoSrc] = useState<string | null>(null);
    const handleDoneEarly = () => {
        stop();
        const themeId = profileForSubAlarms?.themeId || selectedThemeCopy.id;
        setCelebrationVideoSrc(getBonusVideoSrc(themeId));
    };
    const handleCloseCelebration = () => {
        setCelebrationVideoSrc(null);
        reset();
    };

    const content: TimerContentProps = {
        top: {
            leftChildren: (
                <div className="flex items-center gap-2">
                    <HomeButton isVisible={!isDefaultTimer} onClick={selectDefaultTimer} />
                    {isDefaultTimer && <ProfileQuickSwitch />}
                </div>
            ),
            rightChildren: (
                <div className="flex items-center gap-2">
                    <MuteButton currentTheme={themedForDisplay} />
                    <UnitSwitch
                        onClick={toggleUnit}
                        isMinutes={isMinutes}
                        isRunning={isDefaultTimer ? isRunning : true}
                        currentTheme={themedForDisplay}
                    />
                </div>
            ),
        },
        bottom: (
            <div className="flex flex-col gap-3">
                <ControlButtons
                    isMinutes={isMinutes}
                    isRunning={isRunning}
                    isInitialized={isInitialized}
                    currentTheme={themedForDisplay}
                    start={start}
                    stop={stop}
                    reset={reset}
                    add={add}
                />
                {isRunning && (
                    <button
                        onClick={handleDoneEarly}
                        className="w-full rounded-2xl px-6 py-3 text-lg font-extrabold transition-all hover:scale-105 active:scale-95"
                        style={{
                            background: `linear-gradient(135deg, ${themedForDisplay.color.point}, ${themedForDisplay.color.sub || themedForDisplay.color.point}99)`,
                            color: '#fff',
                            boxShadow: `0 4px 20px ${themedForDisplay.color.point}66`,
                        }}
                    >
                        ✅ DONE! I finished early!
                    </button>
                )}
            </div>
        ),
        timerInfo: (
            <>
                <TimeDisplay currentTime={currentTime} className="my-3" />
                {activePhase && (
                    <div
                        className="ryuji-wiggle mb-2 flex items-center gap-2 rounded-full px-4 py-2 text-base font-bold shadow-lg"
                        style={{
                            backgroundColor: activePhase.pointColor || selectedThemeCopy.color.point,
                            color: '#fff',
                        }}
                    >
                        <span className="ryuji-bounce text-2xl">{activePhase.emoji || '🔔'}</span>
                        {activePhase.label}
                    </div>
                )}
            </>
        ),
        timer: (
            <TimerDisplay
                progress={progress}
                currentTheme={themedForDisplay}
                handleDragEvent={handleDragOrClick}
                phases={phases}
                text={
                    activePhase
                        ? `${activePhase.emoji || ''} ${activePhase.label}`.replace('!', '')
                        : selectedThemeCopy.text
                }
            />
        ),
    };

    return (
        <>
            <TimerContent {...content} />
            {celebrationVideoSrc !== null && (
                <BonusVideoOverlay
                    videoSrc={celebrationVideoSrc}
                    currentTheme={themedForDisplay}
                    onClose={handleCloseCelebration}
                />
            )}
        </>
    );
};

export default BaseTimer;
